/**
 * Fee-calculator presentation. Every result shown elsewhere is gross, before
 * fees (decided 2026-10-09); a fee is applied only on /fee-calculator, at the
 * fee the reader enters, and the netting is the backend's
 * (GET /api/public/fee-calculator?fee_rate=, percent of entry per side, fee
 * only). No rate lives here as a default: the calculator opens at the reader's
 * saved fee, else 0% (gross).
 */
// 0.01 steps, so every preset below (0.38 among them) lands exactly. The
// slider covers the venue range; the number box goes up to the 5% ceiling.
export const FEE_SLIDER = { min: 0, max: 1, step: 0.01 } as const

/** The highest fee the backend accepts, percent per side (MAX_CHOSEN_FEE_PCT_PER_SIDE). */
export const MAX_FEE_PCT_PER_SIDE = 5

/**
 * One-click fee settings: what Kraken charges, by tier and product, percent
 * per side. Venue facts for the reader to pick from, never a default. Taker is
 * what a market order pays, maker a resting limit order. Verified against
 * kraken.com/features/fee-schedule on 2026-10-09 (Tier 1 taker 0.80 / maker
 * 0.40, Tier 2 taker 0.60, Tier 3 taker 0.38 at $10K volume or $20K assets
 * on platform, Tier 5 taker 0.30, 1% on Kraken app instant trades); re-check
 * them with TRADING_FEE_VERIFIED_AT (CLAUDE.md) and update
 * KRAKEN_FEES_CHECKED_NOTE with the date.
 */
export const KRAKEN_FEE_PRESETS: { label: string; fee: number; note?: string }[] = [
  { label: 'Kraken Pro entry — market orders (taker)', fee: 0.8 },
  { label: 'Kraken Pro entry — limit orders (maker)', fee: 0.4 },
  { label: '$2.5K volume — market orders (taker)', fee: 0.6 },
  { label: '$10K volume or $20K balance — market orders (taker)', fee: 0.38 },
  { label: '$50K+ volume — market orders (taker)', fee: 0.3 },
  { label: 'Kraken app instant trades', fee: 1.0, note: '(+ spread, not included)' },
]

/** Shown under the presets: where the numbers come from and when they were checked. */
export const KRAKEN_FEES_CHECKED_NOTE =
  "Kraken's published fees, checked Oct 9, 2026 — confirm current fees at kraken.com."

/** "0.80%", or "0.075%" when a third decimal matters. */
export const formatFeePct = (fee: number) => {
  const fine = fee.toFixed(3)
  return `${fine.endsWith('0') ? fee.toFixed(2) : fine}%`
}

/** A typed fee, kept to the 0.001% the number box allows. */
export const roundFee = (fee: number) => Math.round(fee * 1000) / 1000

/** A fee the backend will accept: a finite number from 0 to the ceiling. */
export const isValidFee = (fee: number) => Number.isFinite(fee) && fee >= 0 && fee <= MAX_FEE_PCT_PER_SIDE

/** How long a fee slider must rest before the record is re-fetched at it. */
export const FEE_REFETCH_DEBOUNCE_MS = 300
